import { env } from '@/config/env';
import {
  PipelineFilterState,
  PipelineMetricsSummary,
  PipelineRecord,
  PipelineStageKey,
} from '@/types/domain/pipeline';
import { MOCK_PURCHASE_REQUESTS } from '@/mocks/database';
import { processQuery, getEntityById } from './pagination';
import { notifyGlobal } from '@/context/NotificationContext';
import { mockPurchaseRequestService } from './MockPurchaseRequestService';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const STAGE_KEYS: PipelineStageKey[] = [
  'REQUEST',
  'APPROVAL',
  'SAP_PR',
  'RFQ',
  'QUOTATION',
  'SUPPLIER',
  'PO',
  'DELIVERY',
  'GRN',
  'INVOICE',
  'PAYMENT',
  'CLOSED',
];

const GENERATED_PIPELINE_RECORDS: PipelineRecord[] = MOCK_PURCHASE_REQUESTS.map((req, i) => {
  const isReq1003 = req.id === 'REQ-1003' || req.requestNumber === 'REQ-1003' || i === 2;
  const isReq1001 = req.id === 'REQ-1001' || req.requestNumber === 'REQ-1001' || i === 0;
  const currentStage = isReq1003 ? 'SAP_PR' : STAGE_KEYS[i % STAGE_KEYS.length]!;

  return {
    id: req.id,
    requestNumber: req.requestNumber,
    title: req.title,
    requesterId: req.requesterId,
    requesterName: req.requesterName,
    departmentId: req.departmentId,
    departmentName: req.departmentName,
    plantId: req.plantId,
    plantName: req.plantCode,
    companyCode: '1000',
    costCenter: req.costCenterCode,
    category: req.category,
    capexOpex: req.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX',
    totalAmount: req.totalAmount,
    currency: req.currency,
    priority: req.category === 'CapitalEquipment' ? 'HIGH' : 'MEDIUM',
    createdDate: req.createdAt,
    requiredDate: '2026-10-30',
    expectedDeliveryDate: '2026-11-15',
    currentStage,
    currentStatus: req.status,
    isDelayed: i % 12 === 0,
    delayDays: i % 12 === 0 ? 5 : 0,
    isBlocked: isReq1003 ? true : i % 25 === 0,
    blockedReason: isReq1003 ? 'SAP PR Sync BAPI Exception' : i % 25 === 0 ? 'Vendor Tax Verification Pending' : undefined,
    threeWayMatch: isReq1001 ? { matchStatus: 'MATCHED', priceVariance: 0, quantityVariance: 0, poAmount: 50000, grnAmount: 50000, invoiceAmount: 50000 } : undefined,
    connectedIds: {
      requestId: i === 0 ? 'REQ-1001' : req.id,
      sapPrNumber: i === 0 ? '45000123' : req.sapPrNumber || '45000123',
      nfaId: `NFA-${1000 + i}`,
      rfqId: `rfq-${2000 + i}`,
      quotationId: `quo-${3000 + i}`,
      poNumber: i === 0 ? 'PO-5001' : `PO-${5000 + i}`,
      grnNumber: `GRN-${6000 + i}`,
      invoiceNumber: i === 0 ? 'INV-7001' : `INV-${7000 + i}`,
      paymentId: i === 0 ? 'PAY-8001' : `PAY-${8000 + i}`,
    },
    stages: STAGE_KEYS.map((stgKey, idx) => ({
      key: stgKey,
      label: stgKey.replace('_', ' '),
      status: idx === 0 ? 'COMPLETED' : stgKey === 'SAP_PR' && isReq1003 ? 'ERROR' : 'COMPLETED',
      completedDate: req.createdAt,
      documentNumber: stgKey === 'SAP_PR' ? '45000123' : undefined,
    })),
    timeline: [
      {
        id: `TL-${i + 1}-1`,
        timestamp: req.createdAt,
        stageKey: 'REQUEST',
        systemSource: 'ProcureAI',
        title: 'Requisition Created',
        description: `Requisition ${req.requestNumber} submitted by ${req.requesterName}.`,
        actorName: req.requesterName,
      },
      {
        id: `TL-${i + 1}-2`,
        timestamp: req.createdAt,
        stageKey: 'SAP_PR',
        systemSource: 'SAP',
        title: 'SAP BAPI PR Replicated',
        description: `PR Document ${i === 0 ? '45000123' : req.sapPrNumber || '45000123'} confirmed in SAP S/4HANA.`,
        actorName: 'SAP BAPI Adapter',
      },
    ],
    documentsCount: 2,
    commentsCount: 1,
    source: 'PROCUREAI',
    version: 1,
  };
});

export class MockPipelineService {
  private records: PipelineRecord[] = [...GENERATED_PIPELINE_RECORDS];

  async getPipelineMetrics(): Promise<PipelineMetricsSummary> {
    await delay(env.mockDelayMs);
    const activeTransactions = this.records.filter((r) => r.currentStage !== 'CLOSED').length;
    const totalPipelineValue = this.records.reduce((acc, r) => acc + r.totalAmount, 0);
    const delayedOrders = this.records.filter((r) => r.isDelayed).length;
    const sapSyncExceptions = this.records.filter((r) => r.isBlocked).length;
    const overdueApprovals = this.records.filter((r) => r.currentStage === 'APPROVAL').length;
    const paymentProcessing = this.records.filter((r) => r.currentStage === 'PAYMENT').length;

    return {
      activeTransactions,
      totalPipelineValue,
      delayedOrders,
      sapSyncExceptions,
      overdueApprovals,
      paymentProcessing,
      currency: 'USD',
    };
  }

  async getPipelineRecords(filters?: Partial<PipelineFilterState>): Promise<{ items: PipelineRecord[]; total: number }> {
    await delay(env.mockDelayMs);
    const queryResult = processQuery(this.records, {
      page: filters?.page || 1,
      pageSize: filters?.pageSize || 25,
      search: filters?.search,
      filters: {
        currentStage: filters?.stage,
        status: filters?.status,
        department: filters?.department,
        plant: filters?.plant,
        category: filters?.category,
        priority: filters?.priority,
      },
    });

    return { items: queryResult.data, total: queryResult.total };
  }

  async getPipelineRecordById(id: string): Promise<PipelineRecord | null> {
    await delay(env.mockDelayMs);
    const found = getEntityById(this.records, id, 'id') || getEntityById(this.records, id, 'requestNumber');
    if (found) return found;
    if (this.records.length > 0) {
      const fallback = Object.assign({}, this.records[0], { id, requestNumber: id }) as PipelineRecord;
      this.records.unshift(fallback);
      return fallback;
    }
    return null;
  }

  async retrySapSync(id: string): Promise<PipelineRecord> {
    await delay(env.mockDelayMs);
    const rec = await this.getPipelineRecordById(id);
    if (!rec) throw new Error('Pipeline record not found');
    rec.isBlocked = false;
    rec.blockedReason = undefined;
    rec.currentStage = 'RFQ';
    const sapStage = rec.stages.find((s) => s.key === 'SAP_PR');
    if (sapStage) {
      sapStage.status = 'COMPLETED';
      sapStage.documentNumber = '45000991';
    }
    notifyGlobal('SAP Sync Retried', `Retried SAP synchronization for ${rec.requestNumber}.`, 'System');
    return rec;
  }

  syncPipelineRecord(record: Partial<PipelineRecord> & { id: string }): PipelineRecord {
    const idx = this.records.findIndex(
      (r) => r.id === record.id || (record.requestNumber && r.requestNumber === record.requestNumber)
    );

    // Look up PR from in-memory service or mock database to ensure all fields are populated
    const pr =
      mockPurchaseRequestService.findRequestInMemory(record.id) ||
      (record.requestNumber ? mockPurchaseRequestService.findRequestInMemory(record.requestNumber) : undefined) ||
      MOCK_PURCHASE_REQUESTS.find((p) => p.id === record.id || (record.requestNumber && p.requestNumber === record.requestNumber));

    const seq =
      (record.requestNumber || pr?.requestNumber || record.id).replace(/[^0-9]/g, '') ||
      String(Date.now()).slice(-4);

    if (idx >= 0) {
      const existing = this.records[idx]!;
      const merged: PipelineRecord = {
        ...existing,
        ...record,
        title: record.title || existing.title || pr?.title || `Procurement Requisition ${seq}`,
        requesterName: record.requesterName || existing.requesterName || pr?.requesterName || 'Sarah Jenkins',
        requesterId: record.requesterId || existing.requesterId || pr?.requesterId || 'usr-101',
        departmentName: record.departmentName || existing.departmentName || pr?.departmentName || 'Information Technology',
        departmentId: record.departmentId || existing.departmentId || pr?.departmentId || 'd1',
        category: record.category || existing.category || pr?.category || 'CapitalEquipment',
        capexOpex: record.capexOpex || existing.capexOpex || (pr?.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX'),
        totalAmount:
          record.totalAmount !== undefined && record.totalAmount !== null
            ? record.totalAmount
            : existing.totalAmount !== undefined && existing.totalAmount !== null && existing.totalAmount > 0
            ? existing.totalAmount
            : pr?.totalAmount || 0,
        currency: record.currency || existing.currency || pr?.currency || 'USD',
        costCenter: record.costCenter || existing.costCenter || pr?.costCenterCode || 'CC-IT-101',
        plantName: record.plantName || existing.plantName || pr?.plantCode || 'PLANT-1000',
        currentStage: record.currentStage || existing.currentStage || 'APPROVAL',
        currentStatus: record.currentStatus || existing.currentStatus || 'In Progress',
        connectedIds: {
          ...existing.connectedIds,
          ...record.connectedIds,
          sapPrNumber: record.connectedIds?.sapPrNumber || existing.connectedIds?.sapPrNumber || `45000${seq}`,
        },
        stages: record.stages || existing.stages,
      };
      this.records.splice(idx, 1);
      this.records.unshift(merged);
      return merged;
    }

    // New record: populate all required fields with complete data
    const newRec: PipelineRecord = {
      id: record.id,
      requestNumber: record.requestNumber || pr?.requestNumber || `REQ-${seq}`,
      title: record.title || pr?.title || `Procurement Requisition ${seq}`,
      requesterId: record.requesterId || pr?.requesterId || 'usr-101',
      requesterName: record.requesterName || pr?.requesterName || 'Sarah Jenkins',
      departmentId: record.departmentId || pr?.departmentId || 'd1',
      departmentName: record.departmentName || pr?.departmentName || 'Information Technology',
      plantId: record.plantId || pr?.plantId || 'p1',
      plantName: record.plantName || pr?.plantCode || 'PLANT-1000',
      companyCode: record.companyCode || '1000',
      costCenter: record.costCenter || pr?.costCenterCode || 'CC-IT-101',
      category: record.category || pr?.category || 'CapitalEquipment',
      capexOpex: record.capexOpex || (pr?.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX'),
      totalAmount:
        record.totalAmount !== undefined && record.totalAmount !== null
          ? record.totalAmount
          : pr?.totalAmount || 0,
      currency: record.currency || pr?.currency || 'USD',
      priority: record.priority || (Number(record.totalAmount || pr?.totalAmount || 0) > 50000 ? 'HIGH' : 'MEDIUM'),
      createdDate: record.createdDate || pr?.createdAt || new Date().toISOString(),
      requiredDate: record.requiredDate || pr?.requiredDate || '2026-10-30',
      expectedDeliveryDate: record.expectedDeliveryDate || '2026-11-15',
      currentStage: record.currentStage || 'APPROVAL',
      currentStatus:
        record.currentStatus ||
        (record.currentStage === 'RFQ' ? 'NFA / RFQ Sourcing in Progress' : 'Pending Manager Approval'),
      isDelayed: record.isDelayed ?? false,
      delayDays: record.delayDays ?? 0,
      isBlocked: record.isBlocked ?? false,
      blockedReason: record.blockedReason,
      connectedIds: {
        requestId: record.id,
        sapPrNumber: `45000${seq}`,
        ...record.connectedIds,
      },
      stages: record.stages || [
        { key: 'REQUEST', label: 'Requisition Created', status: 'COMPLETED', documentNumber: record.requestNumber || pr?.requestNumber || `REQ-${seq}`, completedDate: new Date().toISOString() },
        { key: 'APPROVAL', label: 'Department Manager Approval', status: record.currentStage === 'RFQ' ? 'COMPLETED' : 'IN_PROGRESS' },
        { key: 'SAP_PR', label: 'SAP PR Sync', status: record.currentStage === 'RFQ' ? 'COMPLETED' : 'NOT_STARTED', documentNumber: `45000${seq}` },
        { key: 'RFQ', label: 'NFA / RFQ Sourcing', status: record.currentStage === 'RFQ' ? 'IN_PROGRESS' : 'NOT_STARTED' },
        { key: 'QUOTATION', label: 'Quotation Evaluation', status: 'NOT_STARTED' },
        { key: 'SUPPLIER', label: 'Supplier Selection', status: 'NOT_STARTED' },
        { key: 'PO', label: 'Purchase Order', status: 'NOT_STARTED' },
        { key: 'DELIVERY', label: 'Logistics Delivery', status: 'NOT_STARTED' },
        { key: 'GRN', label: 'Warehouse Goods Receipt', status: 'NOT_STARTED' },
        { key: 'INVOICE', label: '3-Way Match Invoice', status: 'NOT_STARTED' },
        { key: 'PAYMENT', label: 'Treasury Settlement', status: 'NOT_STARTED' },
        { key: 'CLOSED', label: 'Lifecycle Closed', status: 'NOT_STARTED' },
      ],
      timeline: [
        {
          id: `TL-${seq}-1`,
          timestamp: record.createdDate || pr?.createdAt || new Date().toISOString(),
          stageKey: 'REQUEST',
          systemSource: 'ProcureAI',
          title: 'Requisition Created',
          description: `Requisition ${record.requestNumber || pr?.requestNumber || `REQ-${seq}`} registered in procurement pipeline.`,
          actorName: record.requesterName || pr?.requesterName || 'Sarah Jenkins',
        },
      ],
      documentsCount: 2,
      commentsCount: 1,
      source: 'PROCUREAI',
      version: 1,
    };
    this.records.unshift(newRec);
    return newRec;
  }
}

export const mockPipelineService = new MockPipelineService();
